import {
  Component,
  input,
  output,
  computed,
  inject,
  signal
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { DropdownModule } from 'primeng/dropdown';
import { TooltipModule } from 'primeng/tooltip';
import { Tarea } from 'src/app/domain/model/tarea/tarea';
import { TareaRepository } from 'src/app/domain/service/tarea/tarea-repository';
import {
  sanitizeText,
  sanitizeTextPreserveSpaces
} from 'src/app/common/input-utils';
import { busquedaTareaPreset } from 'src/app/presentation/shared/table-standardization/table-standard-presets';
import { BaseBusquedaPaginadaDialog } from 'src/app/presentation/shared/busquedas/base-busqueda-paginada-dialog';
import { BusquedaPaginadorComponent } from 'src/app/presentation/shared/busquedas/busqueda-paginador.component';

/**
 * Filtros para búsqueda de tareas.
 * Migrado del legacy: UtilidadesBuscadores.buscarTarea()
 *
 * Legacy BusquedaActivity usa start/length para paginación.
 */
export interface FiltroTarea {
  codigo?: string;
  nombre?: string;
  start?: number;
  length?: number;
}

/**
 * Resultado de tarea seleccionada
 */
export interface TareaSeleccionada {
  nmconsecutivo: number;
  cdservicio: string;
  dsservicio: string;
  dsclasificacion?: string;
  /**
   * Código de clasificación de la homologación (TPSC_CLAS_HOMOLOG_CLIENT.CDCLASIFICACION).
   * Es el "tipo de orden de servicio" con el que se decide qué tipos de cita
   * son válidos (Capacitación = '3' → solo Campo).
   */
  cdclasificacion?: string;
  /** Formato legacy: "CODIGO - NOMBRE_TAREA" */
  descripcionCompleta: string;
}

/**
 * Diálogo de búsqueda de tareas/homologaciones.
 * Replica exactamente el legacy GWT: UtilidadesBuscadores.buscarTarea()
 *
 * Filtros:
 * - Código
 * - Tarea (nombre)
 *
 * Resultado formato: "CODIGO - NOMBRE_TAREA - CLASIFICACION"
 *
 * @author Ingeneo - Migración GWT a Angular
 */
@Component({
  selector: 'app-busqueda-tarea-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    TableModule,
    InputTextModule,
    ButtonModule,
    ProgressSpinnerModule,
    DropdownModule,
    TooltipModule,
    BusquedaPaginadorComponent
  ],
  styleUrls: ['../../shared/styles/grid-table.styles.scss'],
  template: `
    <p-dialog
      [header]="titulo() || 'Filtros de Búsqueda'"
      [(visible)]="visible"
      [modal]="true"
      [style]="{ width: '92vw', maxWidth: '50rem' }"
      [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
      [breakpoints]="{ '1024px': '88vw', '768px': '92vw', '640px': '96vw' }"
      [closable]="true"
      (onHide)="onCancelar()"
    >
      <!-- Sección Filtros de Búsqueda -->
      <div
        class="border dark:border-gray-600 rounded p-4 mb-4 bg-gray-50 dark:bg-gray-800"
      >
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <!-- Código -->
          <div>
            <label class="block text-sm text-gray-600 mb-1">Código</label>
            <input
              pInputText
              [(ngModel)]="filtro.codigo"
              (input)="sanitizarCampo('codigo')"
              class="w-full"
              maxlength="20"
              (keyup.enter)="buscar()"
            />
          </div>

          <!-- Tarea -->
          <div>
            <label class="block text-sm text-gray-600 mb-1">Tarea</label>
            <input
              pInputText
              [(ngModel)]="filtro.nombre"
              (input)="sanitizarCampo('nombre')"
              class="w-full"
              maxlength="100"
              (keyup.enter)="buscar()"
            />
          </div>
        </div>

        <!-- Botones de filtro -->
        <div
          class="flex flex-wrap gap-2 sm:gap-3 mt-4 justify-end sm:justify-start w-full"
        >
          <p-button
            icon="pi pi-eraser"
            label="Limpiar"
            severity="secondary"
            [outlined]="true"
            size="small"
            styleClass="min-h-[44px]"
            (onClick)="limpiarFiltros()"
          />
          <p-button
            icon="pi pi-search"
            label="Buscar"
            size="small"
            styleClass="min-h-[44px]"
            (onClick)="buscar()"
            [disabled]="loading()"
          />
        </div>
      </div>

      <!-- Mensaje de estado -->
      @if (mensaje()) {
        <div
          class="p-3 mb-3 rounded"
          [ngClass]="{
            'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200':
              mensajeTipo() === 'warning',
            'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200':
              mensajeTipo() === 'info',
            'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200':
              mensajeTipo() === 'error'
          }"
        >
          <i
            class="pi mr-2"
            [ngClass]="{
              'pi-exclamation-triangle': mensajeTipo() === 'warning',
              'pi-info-circle': mensajeTipo() === 'info',
              'pi-times-circle': mensajeTipo() === 'error'
            }"
          ></i>
          {{ mensaje() }}
        </div>
      }

      <!-- Loading -->
      @if (loading()) {
        <div class="flex justify-center p-4">
          <p-progressSpinner
            strokeWidth="4"
            [style]="{ width: '50px', height: '50px' }"
          ></p-progressSpinner>
        </div>
      }

      <!-- Tabla de resultados -->
      @if (!loading() && items().length > 0) {
        <div
          class="font-medium text-gray-700 dark:text-gray-200 p-3 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-sm flex justify-between items-center"
        >
          <span>Listado de Tareas</span>
          @if (totalRegistros() > 0) {
            <span class="text-xs text-gray-500 dark:text-gray-400"
              >Registros del {{ registroInicio() }} hasta el
              {{ registroFin() }} de {{ totalRegistros() }}</span
            >
          }
        </div>
        <div class="md:hidden space-y-3 p-3">
          @for (item of items(); track item.nmconsecutivo) {
            <div
              class="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-900"
            >
              <div class="mb-3 min-w-0">
                <div class="text-xs text-gray-500 dark:text-gray-400">
                  Tarea
                </div>
                <div
                  class="font-semibold text-gray-800 dark:text-gray-100 whitespace-normal break-words"
                  [attr.title]="tituloTabla(formatearNombreTarea(item))"
                >
                  {{ textoTabla(formatearNombreTarea(item)) }}
                </div>
              </div>
              <div class="grid grid-cols-1 gap-2 text-sm mb-3">
                <div>
                  <span class="text-gray-500 dark:text-gray-400">Código:</span>
                  <span class="text-gray-800 dark:text-gray-200">{{
                    textoTabla(codigoDisplay(item))
                  }}</span>
                </div>
              </div>
              <p-button
                label="Seleccionar"
                icon="pi pi-check"
                size="small"
                styleClass="w-full min-h-[44px]"
                (onClick)="seleccionarItem(item)"
              />
            </div>
          }
        </div>

        <div class="hidden md:block">
          <div
            role="region"
            [attr.aria-label]="tableConfig().a11y.tableAriaLabel"
            class="sr-only"
          ></div>
          <p-table
            [resizableColumns]="true"
            columnResizeMode="expand"
            [value]="items()"
            [scrollable]="true"
            [responsiveLayout]="'scroll'"
            [scrollHeight]="tableConfig().responsive.scrollHeight || '300px'"
            [selectionMode]="tableSelectionMode()"
            [(selection)]="seleccionado"
            (onRowSelect)="onRowSelect($event)"
            [tableStyle]="{
              'min-width': tableConfig().responsive.minWidth || 'max-content'
            }"
            [dataKey]="
              (tableConfig().selection.dataKey || 'nmconsecutivo').toString()
            "
            [styleClass]="tableStyleClass()"
          >
            <ng-template pTemplate="header">
              <tr>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Código">Código</span>
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Tarea">Tarea</span>
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Seleccionar"
                    >Seleccionar</span
                  >
                </th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-item>
              <tr [pSelectableRow]="item">
                <td>
                  <span
                    class="grid-cell-text"
                    [attr.title]="tituloTabla(codigoDisplay(item))"
                    >{{ textoTabla(codigoDisplay(item)) }}</span
                  >
                </td>
                <td>
                  <span
                    class="grid-cell-text"
                    [attr.title]="tituloTabla(formatearNombreTarea(item))"
                    >{{ textoTabla(formatearNombreTarea(item)) }}</span
                  >
                </td>
                <td>
                  <button
                    pButton
                    label="Seleccionar"
                    class="p-button-sm p-button-text"
                    (click)="seleccionarItem(item)"
                  ></button>
                </td>
              </tr>
            </ng-template>
          </p-table>
        </div>

        <app-busqueda-paginador
          [currentPage]="paginaActual()"
          [totalPages]="totalPaginas()"
          [hasPrevious]="hasPrevious()"
          [hasNext]="hasNext()"
          [pageSizeOptions]="pageSizeOptions"
          [selectedPageSize]="selectedPageSize"
          (pageSizeChange)="onSelectedPageSizeChange($event)"
          (first)="irPrimeraPagina()"
          (previous)="loadPreviousPage()"
          (next)="loadNextPage()"
          (last)="irUltimaPagina()"
        ></app-busqueda-paginador>
      }

      @if (!loading() && busquedaRealizada && items().length === 0) {
        <div class="text-center text-gray-500 p-4 border rounded">
          No hay datos
        </div>
      }

      <!-- Botones footer -->
      <ng-template pTemplate="footer">
        <div
          class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full"
        >
          <p-button
            label="Cerrar"
            icon="pi pi-times"
            [outlined]="true"
            styleClass="min-h-[44px]"
            (onClick)="onCancelar()"
          ></p-button>
        </div>
      </ng-template>
    </p-dialog>
  `
})
export class BusquedaTareaDialogComponent extends BaseBusquedaPaginadaDialog<Tarea> {
  private readonly tareaRepository = inject(TareaRepository);
  titulo = input<string>('');
  /** Legacy: Sin Orientación agrega CSV.FEBAJA IS NULL y HO.FEBAJA IS NULL */
  filtrarFebaja = input<boolean>(false);

  // === Outputs ===
  onSelect = output<TareaSeleccionada>();
  onClose = output<void>();

  // === State ===
  visible = true;
  filtro: FiltroTarea = {};

  /** Sanitización XSS/SQLi (seguridad-girop §3) para filtros de texto libre */
  sanitizeText = sanitizeText;
  seleccionado: Tarea | null = null;

  /**
   * Paginación Keyset/Cursor (contrato-paginacion-keyset-lote4.md, apéndice
   * "Lote 4 completado"). El backend SÍ sigue devolviendo totalElements
   * para este endpoint, por lo que se conserva el conteo total. Lo que se
   * pierde es el salto a página arbitraria; "primera"/"última" siguen
   * funcionando porque el backend soporta direction=PREVIOUS sin cursor
   * para llegar a la última página.
   */
  private nextCursor: string | null = null;
  private previousCursor: string | null = null;

  readonly tableConfig = computed(() =>
    busquedaTareaPreset.createConfig({
      selection: {
        mode: 'single',
        dataKey: 'nmconsecutivo',
        selected: this.seleccionado as unknown as Record<string, unknown> | null
      },
      style: {
        tableClassName: 'tabla-busqueda-tarea',
        striped: true,
        gridlines: false,
        resizableColumns: true,
        rowHover: false
      },
      state: {
        loading: this.loading(),
        emptyMessage: 'No hay datos'
      },
      responsive: {
        mode: 'hybrid',
        minWidth: 'max-content',
        scrollHeight: '300px',
        cardBreakpointPx: 768
      },
      a11y: {
        tableAriaLabel: 'Tabla de busqueda de tareas',
        announceSelection: true,
        announcePageChanges: true,
        focusFirstActionOnRowSelect: false
      }
    })
  );

  readonly tableStyleClass = this.buildTableStyleClass(this.tableConfig);

  readonly tableSelectionMode = this.buildTableSelectionMode(this.tableConfig);

  /**
   * Formatea el nombre de la tarea como en el legacy:
   * "CDSERVICIO - NOMBRE_TAREA - CLASIFICACION" (como legacy)
   */
  // Legacy buscarTarea: Código=HO.NMCONSECUTIVO, Tarea=CDSERVICIO-DSSERVICIO-DSCLASIFICACION
  // Legacy Sin OC: Código=CSV.CDSERVICIO, Tarea=DSSERVICIO-DSCLASIFICACION
  codigoDisplay(item: Tarea): string | number {
    return this.filtrarFebaja() ? item.cdservicio : item.nmconsecutivo;
  }

  formatearNombreTarea(item: Tarea): string {
    if (this.filtrarFebaja()) {
      return [item.dsservicio, item.dsclasificacion].filter(Boolean).join(' - ');
    }
    return [item.cdservicio, item.dsservicio, item.dsclasificacion].filter(Boolean).join(' - ');
  }

  /**
   * Construye el texto de búsqueda a partir de los filtros.
   */
  private construirTextoBusqueda(): string {
    const partes: string[] = [];
    if (this.filtro.codigo?.trim()) {
      partes.push(this.filtro.codigo.trim());
    }
    if (this.filtro.nombre?.trim()) {
      partes.push(this.filtro.nombre.trim());
    }
    return partes.join(' ');
  }

  /**
   * Ejecuta la búsqueda con paginación.
   * Ahora el componente hace la búsqueda internamente.
   */
  async buscar(): Promise<void> {
    this.sanitizarCampo('codigo', true);
    this.sanitizarCampo('nombre', true);
    this.mensaje.set('');
    this.busquedaRealizada = true;
    // Búsqueda nueva: siempre página 1
    await this.buscarPagina(null, 'NEXT', 1);
  }

  /**
   * Sanitización XSS de texto libre (seguridad-girop §3). Mientras se teclea
   * conserva los espacios; antes de buscar también los recorta.
   */
  sanitizarCampo(campo: 'codigo' | 'nombre', recortar = false): void {
    const valor = this.filtro[campo];
    if (valor === undefined) return;
    this.filtro[campo] = recortar
      ? sanitizeText(valor)
      : sanitizeTextPreserveSpaces(valor);
  }

  // Los botones Anterior/Siguiente dependen de la respuesta del servidor
  // (skill frontend-girop §17.7), por eso se sobreescriben los derivados de
  // la base (que los calcula desde la página actual).
  override hasPrevious = signal(false);
  override hasNext = signal(false);

  /** Cursor devuelto por el backend para pedir la página siguiente */
  private nextCursorActual = signal<string | null>(null);

  /** Cursor devuelto por el backend para pedir la página anterior */
  private previousCursorActual = signal<string | null>(null);

  /**
   * Ejecuta la búsqueda en el backend por cursor (keyset, ver skill
   * acceso-datos-oracle §7). Una sola llamada devuelve content + totalElements.
   */
  private async buscarPagina(
    cursor: string | null,
    direction: 'NEXT' | 'PREVIOUS' | 'LAST',
    pagina = 1
  ): Promise<void> {
    const codigo = this.filtro.codigo?.trim() || null;
    const nombre = this.filtro.nombre?.trim() || null;
    const size = this.pageSize();

    this.loading.set(true);
    this.mensaje.set('');

    try {
      const response = await this.tareaRepository.buscarPaginado(
        codigo,
        nombre,
        cursor,
        size,
        this.filtrarFebaja(),
        direction
      );

      this.items.set(response.content || []);
      this.totalRegistros.set(response.totalElements || 0);
      this.nextCursorActual.set(response.nextCursor ?? null);
      this.previousCursorActual.set(response.previousCursor ?? null);

      this.hasNext.set(response.hasNext);
      this.hasPrevious.set(response.hasPrevious);

      // paginaActual solo alimenta las etiquetas y cambia únicamente tras una
      // respuesta exitosa. Con LAST se calcula con el total del servidor.
      if (direction === 'LAST') {
        const total = response.totalElements ?? 0;
        this.paginaActual.set(
          size > 0 && total > 0 ? Math.max(1, Math.ceil(total / size)) : 1
        );
      } else {
        this.paginaActual.set(pagina);
      }

      if ((response.content ?? []).length === 0) {
        this.mensaje.set('No se encontraron tareas');
        this.mensajeTipo.set('warning');
      }
    } catch (error) {
      console.error('Error buscando tareas:', error);
      this.mensaje.set(
        this.resolverMensajeErrorBusqueda(error, 'Error al buscar tareas')
      );
      this.mensajeTipo.set('error');
      this.items.set([]);
      this.totalRegistros.set(0);
    } finally {
      this.loading.set(false);
    }
  }

  protected async ejecutarBusqueda(_start: number): Promise<void> {
    await this.buscarPagina(null, 'NEXT', 1);
  }

  override async irPrimeraPagina(): Promise<void> {
    if (this.loading()) {
      return;
    }
    await this.buscarPagina(null, 'NEXT', 1);
  }

  override async loadPreviousPage(): Promise<void> {
    if (this.loading() || !this.hasPrevious()) {
      return;
    }
    await this.buscarPagina(
      this.previousCursorActual(),
      'PREVIOUS',
      Math.max(1, this.paginaActual() - 1)
    );
  }

  override async loadNextPage(): Promise<void> {
    if (this.loading() || !this.hasNext()) {
      return;
    }
    await this.buscarPagina(
      this.nextCursorActual(),
      'NEXT',
      this.paginaActual() + 1
    );
  }

  override async irUltimaPagina(): Promise<void> {
    if (this.loading() || this.totalRegistros() === 0) {
      return;
    }
    await this.buscarPagina(null, 'LAST');
  }

  /**
   * Limpia los filtros y vuelve a buscar (como el legacy).
   */
  limpiarFiltros(): void {
    this.filtro = {};
    this.items.set([]);
    this.clearMensajes();
    this.resetPaginacion();
    this.hasNext.set(false);
    this.hasPrevious.set(false);
    // Legacy: limpiar sin buscar automáticamente (retrieveOnOpen = false)
    // NO llamamos buscar() aquí
  }

  /**
   * Establece los items de la búsqueda (llamado desde el padre).
   */
  setItems(items: Tarea[], total?: number, pagina?: number): void {
    this.items.set(items);
    this.loading.set(false);
    this.totalRegistros.set(total || items.length);
    if (pagina) {
      this.paginaActual.set(pagina);
    }
    // Datos entregados por el padre (sin respuesta de cursor): los flags se
    // calculan con la página y el total recibidos.
    const totalPaginas = this.totalPaginas();
    this.hasPrevious.set(this.paginaActual() > 1);
    this.hasNext.set(this.paginaActual() < totalPaginas);
  }

  /**
   * Establece el estado de carga.
   */
  setLoading(loading: boolean): void {
    this.loading.set(loading);
  }

  /**
   * Muestra un mensaje.
   */
  setMensaje(
    mensaje: string,
    tipo: 'info' | 'warning' | 'error' = 'info'
  ): void {
    this.mensaje.set(mensaje);
    this.mensajeTipo.set(tipo);
    this.loading.set(false);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onRowSelect(event: any): void {
    if (event?.data) {
      this.seleccionado = event.data as Tarea;
    }
  }

  seleccionarItem(item: Tarea): void {
    const descripcionCompleta = this.formatearNombreTarea(item);

    const resultado: TareaSeleccionada = {
      nmconsecutivo: item.nmconsecutivo,
      cdservicio: item.cdservicio,
      dsservicio: item.dsservicio,
      dsclasificacion: item.dsclasificacion,
      cdclasificacion: item.cdclasificacion,
      descripcionCompleta
    };
    this.onSelect.emit(resultado);
    this.visible = false;
  }

  onCancelar(): void {
    this.visible = false;
    this.onClose.emit();
  }

}
